import React, { useEffect, useState } from 'react';
import { Combobox, Input, InputProps, Option, Textarea, TextareaProps } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { ErrorMessageComponent } from '../../../Error/ErrorMessageComponent';
import * as ComponentFunctions from './StageTypeForm.Functions';
import { ContractStageType } from '../../../../models/Entities/Contracts/ContractStageType';
import { GuideDrawer } from '../../../../theme/components/GuideDrawer';
import { GuideField } from '../../../../theme/components/GuideField';

interface FormValidation {
    success: boolean;
    errors: string[];
}

export const ContractStageTypeFormComponent = (props: ComponentFunctions.Props) => {
    const [submittedForm, setSubmittedForm] = useState<boolean>(false);
    const [FormValidationStatus, setFormValidationStatus] = useState<FormValidation | undefined>();
    const [name, setName] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [relatedWords, setRelatedWords] = useState<string>('');
    const [type, setType] = useState<number>(0);
    const [priceCategory, setPriceCategory] = useState<number>(0);
    const [t] = useTranslation();
    const [selectedType, setSelectedType] = useState<string>('');

    useEffect(() => {
        if (props.StageTypeId) props.GetContractStageTypeById(props.StageTypeId);
    }, [props.StageTypeId]);

    useEffect(() => {
        if (submittedForm && FormValidationStatus?.success && props.isSavedSuccessfully) {
            ResetForm();
            props.dismissPanel();
        }
    }, [submittedForm, props.isSavedSuccessfully]);

    const options = [
        { value: "0", text: t('stageTypeForm.props.typeProps.screening') },
        { value: "1", text: t('stageTypeForm.props.typeProps.random') },
        { value: "2", text: t('stageTypeForm.props.typeProps.treatment') },
        { value: "3", text: t('stageTypeForm.props.typeProps.followUp') },
        { value: "4", text: t('stageTypeForm.props.typeProps.preScreening') }
    ];
    useEffect(() => {
        if (props.StageType?.id) {
            setName(props.StageType.name);
            setDescription(props.StageType.description ?? '');
            setRelatedWords(props.StageType.relatedWords ?? '');
            setType(props.StageType.type ?? 0);
            setPriceCategory(props.StageType.priceCategory ?? 0);

            const selectedOption = options.find(option => option.value === String(props.StageType?.type ?? 0))?.text ?? '';
            setSelectedType(selectedOption);
        } else {
            setName('');
            setDescription('');
            setRelatedWords('');
            setType(0);
            setPriceCategory(0);
            setSelectedType('');
        }
    }, [props.StageTypeId, props.StageType?.id, t]);

    const ResetForm = () => {
        setSubmittedForm(false);
        setName('');
        setDescription('');
        setRelatedWords('');
        setType(0);
        setPriceCategory(0);
        setSelectedType('');
    };

    const onChangeName: InputProps['onChange'] = (event, data) => {
        setName(data?.value || '');
    };
    const onChangeDescription: TextareaProps['onChange'] = (event, data) => {
        setDescription(data?.value || '');
    };
    const onChangeRelatedWords: InputProps['onChange'] = (event, data) => {
        setRelatedWords(data?.value || '');
    };

    const ValidateForm = (): boolean => {
        const errors: string[] = [];
        if (!name) {
            errors.push(t('stageTypeForm.props.requiredName'));
        }
        if (errors.length > 0) {
            setFormValidationStatus({ success: false, errors });
            return false;
        }
        setFormValidationStatus({ success: true, errors: [] });
        return true;
    }

    const HandleSaveForm = () => {
        setSubmittedForm(true);
        if (ValidateForm()) {
            const values: Partial<ContractStageType> = {
                id: props.StageType?.id,
                name,
                description,
                relatedWords,
                type,
                priceCategory
            };
            if (props.StageType) {
                props.UpdateContractStageType(props.StageType.id, values);
            } else {
                props.CreateContractStageType(values);
            }
        }
    };

    const HandleCancelForm = () => {
        ResetForm();
        props.dismissPanel();
    };

    return (
        <GuideDrawer
            open={props.isOpen}
            onDismiss={props.dismissPanel}
            preventDismiss={!!props.isSaving}
            size="medium"
            title={props.StageType ? t('stageTypeForm.title.edit') : t('stageTypeForm.title.create')}
            secondaryAction={{
                label: t('stageTypeForm.footer.cancelButton'),
                onClick: HandleCancelForm,
                disabled: props.isSaving
            }}
            primaryAction={{
                label: t('stageTypeForm.footer.saveButton'),
                onClick: HandleSaveForm,
                loading: props.isSaving
            }}
            testId="stage-type-form-drawer"
        >
            {props.error && (
                <ErrorMessageComponent Error={props.error} />
            )}
            <GuideField
                required
                label={t('stageTypeForm.props.name')}
                error={submittedForm && !name ? t('stageTypeForm.props.requiredName') : undefined}
            >
                <Input value={name} onChange={onChangeName} />
            </GuideField>
            <GuideField label={t('stageTypeForm.props.description')}>
                <Textarea value={description} onChange={onChangeDescription} resize="vertical" />
            </GuideField>
            <GuideField label={t('stageTypeForm.props.relatedWords')}>
                <Input value={relatedWords} onChange={onChangeRelatedWords} />
            </GuideField>
            <GuideField label={t('stageTypeForm.props.typeLabel')}>
                <Combobox
                    placeholder={t('stageTypeForm.props.type.placeholder')}
                    value={selectedType}
                    onOptionSelect={(_, data) => {
                        const selectedOption = options?.find(option => option.text === data.optionValue);
                        if (selectedOption) {
                            setType(Number(selectedOption.value));
                            setSelectedType(selectedOption.text);
                        } else {
                            setType(0);
                            setSelectedType('');
                        }
                    }}
                >
                    {options.map(option => (
                        <Option key={option.value} value={option.text}>
                            {option.text}
                        </Option>
                    ))}
                </Combobox>
            </GuideField>
        </GuideDrawer>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ContractStageTypeFormComponent as any);
